import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { getInspectionById, setCurrentCreatedInspection } from "src/store/inspectionSlice";
import { setInspectionResults } from "src/store/inspectionResultsSlice";
import { getTemplateById } from "src/store/inspectionTemplateSlice";
import QuestionsPage from "../questions/questionsPage";
import InspectionDetailsView from "./inspectionDetailsView";
import { Column, Text } from "@amzn/meridian";
import { COLORS } from "src/constants/colors";
import { completedInspectionStatus } from "src/constants/constants";
import { getConfigDrivenQuestionIds } from "./utils/questionIdExtractor";
import { getInspectionWorkflow } from "src/store/workflowSlice";

const InspectionRouter = () => {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inspection, setInspection] = useState<any>(null);
  
  const { selectedInspectionTemplate } = useAppSelector((state) => state.inspectionTemplate);
  const { questions } = useAppSelector((state) => state.workflow);
  useEffect(() => {
    const loadInspection = async () => {
      if (!inspectionId) {
        navigate("/home");
        return;
      }

      try {
        setLoading(true);
        const result = await dispatch(getInspectionById(inspectionId)).unwrap();
        setInspection(result);

        // Load inspection results into Redux
        dispatch(setInspectionResults(result.results || []));

        // Set current inspection for incomplete inspections
        if (result.status !== completedInspectionStatus) {
          dispatch(setCurrentCreatedInspection({
            inspectionId: result.inspectionId,
            templateId: result.templateId,
            templateName: result.templateName,
            siteCode: result.siteCode,
            status: result.status,
            createdBy: result.createdBy,
            createdAt: result.createdAt,
            complianceStats: result.complianceStats || {
              compliantCount: 0,
              questionCount: 0,
            },
          }));
        }

        // Fetch the full template details with questions
        if(result.templateId === 'WorkflowDriven') {
          await dispatch(getInspectionWorkflow());
        } else {
          await dispatch(getTemplateById(result.templateId));
        }
      } catch (error) {
        console.error("Failed to load inspection:", error);
        navigate("/home");
      } finally {
        setLoading(false);
      }
    };

    loadInspection();
  }, [inspectionId, dispatch, navigate]);

  if (loading) {
    return (
      <Column
        height="100vh"
        alignmentVertical="center"
        alignmentHorizontal="center"
        backgroundColor={COLORS.DARKER_WHITE}
      >
        <Text type="h300">Loading inspection...</Text>
      </Column>
    );
  }

  if (!inspection) {
    return (
      <Column
        height="100vh"
        alignmentVertical="center"
        alignmentHorizontal="center"
        backgroundColor={COLORS.DARKER_WHITE}
      >
        <Text type="h300">Inspection not found</Text>
      </Column>
    );
  }

  // Check if inspection is completed
  const isCompleted = inspection.status === completedInspectionStatus;

  // If not completed, show QuestionsPage for continuation
  if (!isCompleted) {
    return <QuestionsPage />;
  }

  // If completed, show InspectionDetailsView
  const hasPendingResults = inspection.results?.some(
    (r: any) => r.aiResult === "PENDING"
  );

  // Determine mode based on pending results
  const mode = hasPendingResults ? "view-only" : "view-editable";

  const handleEdit = () => {
    // Navigate back to questions page for editing
    navigate(`/templates/${inspection.templateId}`);
  };

  const handleBack = () => {
    navigate("/completedInspections");
  };

  return (
    <InspectionDetailsView
      mode={mode}
      templateName={inspection.templateName}
      inspectionResults={inspection.results || []}
      questions={inspection.templateId !== 'ConfigDriven' ? selectedInspectionTemplate?.questions : questions}
      inspectionId={inspection.inspectionId}
      onBack={handleBack}
      onEdit={mode === "view-editable" ? handleEdit : undefined}
      showProgressBreadcrumb={false}
    />
  );
};

export default InspectionRouter;
